import { faVectorSquare, faSquare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  DrawButton,
  LayerTree,
  MapComponent,
  MapContext,
} from "@terrestris/react-geo";
import * as turf from "@turf/turf";
import { Checkbox, Col, Descriptions, Divider, List, Row, Space } from "antd";
import { CheckboxValueType } from "antd/lib/checkbox/Group";
import CommonOverlayComponent from "commons/CommonOverlayComponent";
import CommonCard from "commons/Layout/CommonCard";
import { openNotification } from "commons/Notifications";
import PdfExport from "commons/Pdf/PdfExport";
import PdfStatisticsForestView from "commons/Pdf/templates/pdfStatisticsForestView";
import { XMLParser } from "fast-xml-parser";
import GeoJSON from "ol/format/GeoJSON";
import TileLayer from "ol/layer/Tile";
import OlLayerTile from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import { bbox as bboxStrategy } from "ol/loadingstrategy";
import OlMap from "ol/Map";
import { fromLonLat } from "ol/proj";
import BingMaps from "ol/source/BingMaps";
import OlSourceOsm from "ol/source/OSM";
import TileWMS from "ol/source/TileWMS";
import VectorSource from "ol/source/Vector";
import { Fill, Stroke, Style } from "ol/style";
import OlView from "ol/View";
import { FC, useEffect, useState } from "react";
import ServiceGeoServer from "services/geoServer";
import { degreeToCompass, geoFormatArea } from "utils/helpers/geo_helpers";

const ForestView: FC = () => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [currentStatistic, setCurrentStatistic] = useState([]);
  const [regionsVector, setRegionsVector] = useState(null);
  const [accessiForestali, setAccessiForestali] = useState(null);
  const [intersectedRegionName, setIntersectedRegionName] = useState(null);
  //const [selectedRaster, setSelectedRaster] = useState<CheckboxValueType[]>(['biomassa_2005', 'provvigione_legnosa', 'italia_cai_model', 'italia_forest_mask'])
  const [selectedRaster, setSelectedRaster] = useState<CheckboxValueType[]>([
    "forestview:biomassa_2005",
    "forestview:provvigione_legnosa",
    "forestview:italia_cai_model",
    //'forestview:italia_forest_mask',
    "italy_aspect_slope:italy_aspect_compr",
    "italy_aspect_slope:italy_slope_compr",
    "italy_aspect_slope:racc_ita_30m",
  ]);
  const [map, setMap] = useState(null);
  const [mapImageUrl, setMapImageUrl] = useState(null);
  const [projectArea, setProjectArea] = useState(null);
  const geoServerUrl = process?.env?.REACT_APP_GEOSERVER_URL;

  const latlon = [12.4963655, 41.9027835];

  useEffect(() => {
    // Get Regions Vector
    ServiceGeoServer.getItalianRegionsShape().then((wfsRegions) => {
      const vectorSource = new VectorSource({
        features: new GeoJSON().readFeatures(wfsRegions),
        strategy: bboxStrategy,
      });

      setRegionsVector(
        new VectorLayer({
          source: vectorSource,
          // @ts-ignore
          name: "Regioni",
          style: new Style({
            fill: new Fill({
              color: "rgba(94, 176, 44, 0.1)",
            }),
            stroke: new Stroke({
              color: "rgba(94, 176, 44, 1.0)",
              width: 2,
            }),
          }),
        })
      );
    });

    // Get Accessi Forestali
    setAccessiForestali(
      new TileLayer({
        // @ts-ignore
        name: "Accessibilità",
        source: new TileWMS({
          url: `${geoServerUrl}/wms`,
          params: { LAYERS: "italy_aspect_slope:racc_ita_30m", TILED: true },
          serverType: "geoserver",
          crossOrigin: "Anonymous",
          transition: 0,
        }),
      })
    );
  }, []);

  // Set map
  useEffect(() => {
    if (regionsVector && accessiForestali) {
      setMap(
        new OlMap({
          layers: [
            new OlLayerTile({
              // @ts-ignore
              name: "OSM",
              source: new OlSourceOsm(),
            }),
            new OlLayerTile({
              // @ts-ignore
              name: "Satellite",
              source: new BingMaps({
                key: "AgvK-EavqnKlSbi2mfp_nKxT5RpmBZLr4N9k1ChJeW1v8ePqaUEUSy2WYDllrYRH",
                imagerySet: "Aerial",
              }),
            }),
            regionsVector,
            accessiForestali,
          ],
          view: new OlView({
            //projection: 'EPSG:4326',
            center: fromLonLat(latlon),
            zoom: 7,
          }),
        })
      );
    }
  }, [regionsVector, accessiForestali]);

  // Resize map for pdf
  const resizeMapPdf = () => {
    console.log("RESIZE PDF MAP");
    if (currentStatistic && map) {
      const dims = {
        a0: [1189, 841],
        a1: [841, 594],
        a2: [594, 420],
        a3: [420, 297],
        a4: [297, 210],
        a5: [210, 148],
      };

      // Get map info e size
      const format = "a4";
      const resolution = 150;
      const dim = dims[format];
      const width = Math.round((dim[0] * resolution) / 25.4);
      const height = Math.round((dim[1] * resolution) / 25.4);
      const size = map.getSize();
      const viewResolution = map.getView().getResolution();

      // Set print size
      const printSize = [width, height];
      map.setSize(printSize);
      const scaling = Math.min(width / size?.[0], height / size?.[1]);
      map.getView().setResolution(viewResolution / scaling);

      // Generate pdf and resize original map
      map.once("rendercomplete", function () {
        const mapCanvas = document.createElement("canvas");
        mapCanvas.width = width;
        mapCanvas.height = height;
        const mapContext = mapCanvas.getContext("2d");
        Array.prototype.forEach.call(
          document.querySelectorAll(".ol-layer canvas, canvas.ol-layer"),
          function (canvas) {
            if (canvas.width > 0) {
              const opacity = canvas.parentNode.style.opacity;
              mapContext.globalAlpha = opacity === "" ? 1 : Number(opacity);
              const transform = canvas.style.transform;
              // Get the transform parameters from the style's transform matrix
              const matrix = transform
                .match(/^matrix\(([^\(]*)\)$/)[1]
                .split(",")
                .map(Number);
              // Apply the transform to the export map context
              CanvasRenderingContext2D.prototype.setTransform.apply(
                mapContext,
                matrix
              );
              mapContext.drawImage(canvas, 0, 0);
            }
          }
        );
        mapContext.globalAlpha = 1;

        // Reset original map size
        map.setSize(size);
        map.getView().setResolution(viewResolution);
        setMapImageUrl(mapCanvas.toDataURL("image/jpeg"));
      });
    }
  };

  if (!map) {
    return null;
  }

  const onChangeStatisticRasterOption = (
    checkedValues: CheckboxValueType[]
  ) => {
    setSelectedRaster(checkedValues);
  };

  // Clear layer when start new drawing
  const cleanLayer = () => {
    map.getLayers().forEach((el) => {
      if (el.get("name") === intersectedRegionName) {
        el.getSource().clear();
      }
    });
  };

  // Generate statistics
  const generateStatistics = () => {
    const raccIta = [
      "italy_aspect_slope:rACC_ITA_30m_val1",
      "italy_aspect_slope:rACC_ITA_30m_val2",
      "italy_aspect_slope:rACC_ITA_30m_val3",
    ];

    let totalRaccIta = 0,
      raccItaVal1,
      raccItaVal2,
      raccItaVal3,
      percentRaccIta1,
      percentRaccIta2,
      percentRaccIta3;

    currentStatistic?.map((statRaccIta) => {
      let statRaccItaKey = Object.keys(statRaccIta)[0];
      if (statRaccItaKey === "italy_aspect_slope:rACC_ITA_30m_val1") {
        raccItaVal1 = statRaccIta[statRaccItaKey]?.["feature:count"];
      }
      if (statRaccItaKey === "italy_aspect_slope:rACC_ITA_30m_val2") {
        raccItaVal2 = statRaccIta[statRaccItaKey]?.["feature:count"];
      }
      if (statRaccItaKey === "italy_aspect_slope:rACC_ITA_30m_val3") {
        raccItaVal3 = statRaccIta[statRaccItaKey]?.["feature:count"];
      }
    });

    totalRaccIta = raccItaVal1 + raccItaVal2 + raccItaVal3;
    percentRaccIta1 = (raccItaVal1 / totalRaccIta) * 100;
    percentRaccIta2 = (raccItaVal2 / totalRaccIta) * 100;
    percentRaccIta3 = (raccItaVal3 / totalRaccIta) * 100;

    return currentStatistic?.map((statistic) => {
      let statKey = Object.keys(statistic)[0];
      let title, unit;
      if (raccIta.includes(statKey)) {
        return "";
      }
      switch (statKey) {
        case "forestview:provvigione_legnosa":
          title = "Provvigione Legnosa: GSV - Metri cubi per ettaro l'anno";
          unit = (
            <span>
              m<sup>3</sup>/ha
            </span>
          );
          break;
        case "forestview:italia_forest_mask":
          title = "Copertura boschiva - Metri cubi per ettaro l'anno";
          unit = (
            <span>
              m<sup>3</sup>/ha
            </span>
          );
          break;
        case "forestview:italia_cai_model":
          title =
            "Incremento: CAI - Incremento volume legnoso metri cubi per ettaro l'anno";
          unit = (
            <span>
              m<sup>3</sup>/ha
            </span>
          );
          break;
        case "forestview:biomassa_2005":
          title =
            "Biomassa: AGB - Biomassa Tonnellate (o Megagrammi Mg) per ettaro";
          unit = "Mg/ha";
          break;
        case "italy_aspect_slope:italy_aspect_compr":
          title = "Esposizione";
          unit = "";
          break;
        case "italy_aspect_slope:italy_slope_compr":
          title = "Pendenza";
          unit = "%";
          break;
        case "italy_aspect_slope:racc_ita_30m":
          title = "Accessibilità forestale";
          unit = "";
          break;
        default:
          title = "";
          break;
      }
      return (
        <Descriptions
          key={statKey}
          bordered
          title={<h3>{title}</h3>}
          size={"middle"}
          style={{ paddingBottom: "20px" }}
          column={{ xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
          //extra={<Button type="primary" onClick={() => ExportPdf}>Export Pdf</Button>}
        >
          {/* <Descriptions.Item labelStyle={{ width: '50%' }} label={<b>Totale</b>}>{!isNaN(statistic?.[statKey]?.['feature:sum']) ? statistic?.[statKey]?.['feature:sum']?.toFixed(2) : ''} {unit}</Descriptions.Item> */}
          {statKey !== "forestview:italia_cai_model" &&
            statKey !== "italy_aspect_slope:racc_ita_30m" &&
            statKey !== "italy_aspect_slope:italy_aspect_compr" &&
            statKey !== "italy_aspect_slope:italy_slope_compr" && (
              <>
                <Descriptions.Item
                  key={statKey + "max"}
                  labelStyle={{ width: "50%" }}
                  label={<b>Valore massimo</b>}
                >
                  {!isNaN(statistic?.[statKey]?.["feature:max"])
                    ? statistic?.[statKey]?.["feature:max"]?.toFixed(2)
                    : ""}{" "}
                  {unit}
                </Descriptions.Item>
                <Descriptions.Item
                  key={statKey + "min"}
                  labelStyle={{ width: "50%" }}
                  label={<b>Valore minimo</b>}
                >
                  {!isNaN(statistic?.[statKey]?.["feature:min"])
                    ? statistic?.[statKey]?.["feature:min"]?.toFixed(2)
                    : ""}{" "}
                  {unit}
                </Descriptions.Item>
              </>
            )}

          {statKey === "italy_aspect_slope:racc_ita_30m" ? (
            <Descriptions.Item
              key={statKey + "raccitapercent"}
              labelStyle={{ width: "50%" }}
              label={<b>Valore percentuale</b>}
            >
              <ul key="raccitaPercent">
                <li key="raccita3">
                  Alta {!isNaN(percentRaccIta1) && percentRaccIta1?.toFixed(1)}%
                </li>
                <li key="raccita2">
                  Media {!isNaN(percentRaccIta2) && percentRaccIta2?.toFixed(1)}
                  %
                </li>
                <li key="raccita1">
                  Bassa {!isNaN(percentRaccIta3) && percentRaccIta3?.toFixed(1)}
                  %
                </li>
              </ul>
            </Descriptions.Item>
          ) : (
            <Descriptions.Item
              key={statKey + "avg"}
              labelStyle={{ width: "50%" }}
              label={<b>Valore medio</b>}
            >
              {!isNaN(statistic?.[statKey]?.["feature:avg"])
                ? statKey === "italy_aspect_slope:italy_aspect_compr"
                  ? degreeToCompass(
                      statistic?.[statKey]?.["feature:avg"]?.toFixed(2)
                    )
                  : statistic?.[statKey]?.["feature:avg"]?.toFixed(2)
                : ""}{" "}
              {unit}
            </Descriptions.Item>
          )}
        </Descriptions>
      );
    });
  };

  // Calculate statistic
  const calculateStatistic = async (feature) => {
    // Remove geo_digitize from layers
    map.getLayers().forEach((el) => {
      if (el.get("name") === "react-geo_digitize") {
        el.getSource().clear();
      }
    });

    // Check area
    let area = geoFormatArea(feature.getGeometry(), "km");
    if (area < 0.05) {
      openNotification({
        message: "Errore dimensione area",
        description: `L'area selezionata (${area} km2) deve essere superiore a 0.05 km2.`,
        type: "error",
      });
      return false;
    }

    // Set project area
    setProjectArea(area);
    let turfFeat: any = new GeoJSON().writeFeatureObject(feature);
    let centroid = turf.centroid(turfFeat);
    let xc = centroid.geometry.coordinates[0];
    let yc = centroid.geometry.coordinates[1];

    // Get Intersection Vector
    await ServiceGeoServer.getItalianRegionsShapeIntersect(xc, yc).then(
      (response) => {
        setIntersectedRegionName(response?.features?.[0]?.properties?.DEN_REG);
        // let selectedRegionFeature = formatGeoJSON.writeFeatureObject(response.features);
        const selectedRegionFeature = turf.multiPolygon(
          response.features[0].geometry.coordinates
        );
        const intersection = turf.intersect(selectedRegionFeature, turfFeat);
        const marker = new GeoJSON().readFeature(intersection);

        const sourceIntersectLayer = new VectorSource({
          wrapX: false,
          features: [marker],
        });

        const vectorIntersectLayer = new VectorLayer({
          // @ts-ignore
          name: response?.features?.[0]?.properties?.DEN_REG,
          id: "intersect",
          source: sourceIntersectLayer,
        });

        map.addLayer(vectorIntersectLayer);
      }
    );

    // Calculate statistics
    if (feature) {
      setTimeout(() => {
        //map.removeLayer(vectorIntersectLayer)
        setDrawerVisible(true);

        let geom = feature.getGeometry().transform("EPSG:3857", "EPSG:4326");

        const fullRasterList = [
          ...selectedRaster,
          ...[
            "italy_aspect_slope:rACC_ITA_30m_val1",
            "italy_aspect_slope:rACC_ITA_30m_val2",
            "italy_aspect_slope:rACC_ITA_30m_val3",
          ],
        ];

        // Loop calculate statistics
        fullRasterList.forEach((raster: any) => {
          let resObject = {};
          ServiceGeoServer.calculateForestViewStatistic(
            raster,
            geom.getCoordinates()
          ).then((success) => {
            const parser = new XMLParser();
            let jObj = parser.parse(success);
            let feat =
              jObj?.["wfs:FeatureCollection"]?.["feature:featureMember"]?.[
                "feature:features"
              ];
            resObject[raster] = feat;
            setCurrentStatistic((prevState) => {
              return prevState?.length > 0
                ? [...prevState, resObject]
                : [resObject];
            });
          });
        });

        resizeMapPdf();
      }, 2000);
    }
  };

  return (
    <CommonCard title={<h2>ForestView - navigazione mappa</h2>}>
      <h3>
        Naviga sull'area di interesse, disegna il poligono e visualizza le
        informazioni statistiche della zona selezionata
      </h3>
      <p>
        Le dimensioni minime dell'area non devono essere inferiori a 0,05km2,
        l’area di interesse viene finalizzata con un doppio click
      </p>
      {/* <Divider /> */}
      {/* <PdfButton templateComponent={<PdfStatisticsForestView srcMapImage={mapImageUrl} />} /> */}

      <MapContext.Provider value={map}>
        <Row gutter={16}>
          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
            <DrawButton
              tooltip="Disegna una area per calcolare le statistiche"
              icon={
                <FontAwesomeIcon
                  icon={faVectorSquare}
                  style={{ marginRight: "10px" }}
                />
              }
              pressedIcon={
                <FontAwesomeIcon
                  icon={faVectorSquare}
                  style={{ marginRight: "10px" }}
                />
              }
              name="drawPolygon"
              drawType="Polygon"
              onDrawEnd={({ feature }) => calculateStatistic(feature)}
              onDrawStart={() => cleanLayer()}
            >
              Disegna un poligono
            </DrawButton>
            <Divider />
          </Col>

          <Col xs={24} sm={24} md={24} lg={12} xl={4}>
            <Space
              direction="vertical"
              size="middle"
              style={{ display: "flex" }}
            >
              <span style={{ fontWeight: "bold" }}>
                {"Seleziona visualizzazione mappa:"}
              </span>
              <LayerTree
                map={map}
                filterFunction={(layer) => {
                  return (
                    layer.get("name") !== "react-geo_digitize" &&
                    layer.get("id") !== "intersect"
                  );
                }}
              />
              <Divider style={{ margin: "5px 0" }} />
              <span style={{ fontWeight: "bold" }}>
                {"Seleziona layer per le estrazioni statistiche:"}
              </span>
              <Checkbox.Group
                defaultValue={selectedRaster}
                onChange={onChangeStatisticRasterOption}
              >
                <Row style={{ paddingLeft: "10px" }}>
                  <Col span={24}>
                    <Checkbox value="forestview:biomassa_2005">
                      Biomassa
                    </Checkbox>
                  </Col>
                  <Col span={24}>
                    <Checkbox value="forestview:provvigione_legnosa">
                      Provvigione legnosa
                    </Checkbox>
                  </Col>
                  <Col span={24}>
                    <Checkbox value="forestview:italia_cai_model">
                      Incremento
                    </Checkbox>
                  </Col>
                  {/* <Col span={24}>
                    <Checkbox value="forestview:italia_forest_mask">Copertura boschiva</Checkbox>
                  </Col> */}
                  <Col span={24}>
                    <Checkbox value="italy_aspect_slope:italy_aspect_compr">
                      Esposizione
                    </Checkbox>
                  </Col>
                  <Col span={24}>
                    <Checkbox value="italy_aspect_slope:italy_slope_compr">
                      Pendenza
                    </Checkbox>
                  </Col>
                  <Col span={24}>
                    <Checkbox value="italy_aspect_slope:racc_ita_30m">
                      Accessibilità forestale
                    </Checkbox>
                  </Col>
                </Row>
              </Checkbox.Group>
              <Divider style={{ margin: "5px 0" }} />
              <span style={{ fontWeight: "bold" }}>
                {"Legenda accessibilità:"}
              </span>
              <ul style={{ listStyle: "none", padding: 0 }}>
                <li>
                  {" "}
                  <FontAwesomeIcon
                    icon={faSquare}
                    style={{ marginRight: "10px", color: "#eb1b1a" }}
                  />
                  Bassa accessibilità
                </li>
                <li>
                  <FontAwesomeIcon
                    icon={faSquare}
                    style={{ marginRight: "10px", color: "#ffdf13" }}
                  />
                  Media accessibilità
                </li>
                <li>
                  {" "}
                  <FontAwesomeIcon
                    icon={faSquare}
                    style={{ marginRight: "10px", color: "#21b42f" }}
                  />
                  Alta accessibilità
                </li>
              </ul>
            </Space>
          </Col>
          <Col xs={24} sm={24} md={24} lg={24} xl={20}>
            <MapComponent
              map={map}
              style={{
                height: "80vh",
              }}
            />
          </Col>
        </Row>
      </MapContext.Provider>

      <CommonOverlayComponent
        title={`Statistiche area selezionata ${
          intersectedRegionName ? "(Regione " + intersectedRegionName + ")" : ""
        } - Superifcie ${projectArea} km2`}
        //onSubmit={onSubmit}
        type="modal"
        destroyOnClose={true}
        //permission={DisabledFields}
        visible={drawerVisible}
        width={"70%"}
        //okText="Conferma e Salva"
        onCancel={() => {
          setCurrentStatistic([]);
          setDrawerVisible(false);
        }}
        // formsOpts={{
        //   debug: false,
        //   fields: AirTreeProjectFormFields,
        //   //ref: Form,
        //   hideSubmit: true,
        //   initialValues: currentRecord || undefined,
        // }}
      >
        {currentStatistic.length < selectedRaster.length ? (
          <div>
            Caricamento dati (l'operazione potrebbe richiedere diversi minuti di
            elaborazione)...
          </div>
        ) : (
          <>
            <PdfExport
              templateComponent={
                <PdfStatisticsForestView
                  srcMapImage={mapImageUrl}
                  statistics={currentStatistic}
                  projectArea={projectArea}
                  region={intersectedRegionName}
                />
              }
            />
            <Divider />
            {generateStatistics()}
          </>
        )}
      </CommonOverlayComponent>
    </CommonCard>
  );
};

export default ForestView;
